import { cn } from '@repo/ui/lib/utils'
import { cva, VariantProps } from 'class-variance-authority'
import React from 'react'

// Atom for a basic container element with a tone, shadow, interactive, radius, padding, and border

export type SurfaceProps = {
  as?: React.ElementType
} & React.ComponentPropsWithRef<React.ElementType> &
  VariantProps<typeof surfaceVariants>

const surfaceVariants = cva('', {
  variants: {
    tone: {
      default: 'bg-background-level-1 text-foreground',
      secondary: 'bg-background-level-2 text-foreground',
      'level-1': 'bg-background-level-1 text-foreground',
      'level-2': 'bg-background-level-2 text-foreground',
      'level-3': 'bg-background-level-3 text-foreground',
      'level-4': 'bg-background-level-4 text-foreground',
      critical: 'bg-critical-bg text-critical-text border-critical-border',
      warning: 'bg-warning-bg text-warning-text border-warning-border',
      info: 'bg-info-bg text-info-text border-info-border',
      success: 'bg-success-bg text-success-text border-success-border',
    },
    shadow: {
      none: '',
      sm: 'shadow-sm',
      md: 'shadow-md',
      lg: 'shadow-lg',
      xl: 'shadow-xl',
      '2xl': 'shadow-2xl',
    },
    interactive: {
      true: 'cursor-pointer transition-colors',
      false: '',
    },
    radius: {
      none: '',
      sm: 'rounded-sm',
      md: 'rounded-md',
      lg: 'rounded-lg',
      xl: 'rounded-xl',
    },
    padding: {
      none: '',
      xs: 'p-1',
      sm: 'p-2',
      md: 'p-4',
      lg: 'p-6',
      xl: 'p-8',
    },
    border: {
      true: 'border',
      false: '',
    },
  },
  compoundVariants: [
    {
      tone: 'critical',
      interactive: true,
      className: 'hover:bg-critical-bg-dark hover:border-critical-border-dark',
    },
    {
      tone: 'warning',
      interactive: true,
      className: 'hover:bg-warning-bg-dark hover:border-warning-border-dark',
    },
    {
      tone: 'success',
      interactive: true,
      className: 'hover:bg-success-bg-dark hover:border-success-border-dark',
    },
    {
      tone: 'info',
      interactive: true,
      className: 'hover:bg-info-bg-dark hover:border-info-border-dark',
    },
  ],
  defaultVariants: {
    tone: 'default',
    radius: 'none',
    padding: 'none',
    border: true,
    interactive: false,
    shadow: 'none',
  },
})

export const Surface = React.forwardRef<HTMLElement, SurfaceProps>(
  ({ as: Component = 'div', ...props }, ref) => {
    const { tone, radius, interactive, padding, border, shadow, className, ...rest } = props

    return (
      <Component
        ref={ref}
        className={cn(
          surfaceVariants({ tone, radius, interactive, padding, border, shadow }),
          className,
        )}
        {...rest}
      />
    )
  },
)

Surface.displayName = 'Surface'
